"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Icons, Images } from "@/app/assets/shared";
import type { PortfolioProject } from "../data";

interface FullWidthCardProps {
  project: PortfolioProject;
  theme?: "light" | "dark";
  onClick: () => void;
  imagePriority?: boolean;
}

export default function FullWidthCard({
  project,
  theme = "light",
  onClick,
  imagePriority = false,
}: FullWidthCardProps) {
  const isDark = theme === "dark";
  const titleColor = isDark ? "text-white" : "text-black";
  const descColor = isDark ? "text-white/50" : "text-[#636363]";
  const tagText = isDark ? "text-[#fff]" : "text-[#00040BCC]";
  const tagBg = isDark ? "bg-[rgb(48,48,48,0.5)]" : "bg-black/5";
  const dividerColor = isDark ? "lg:divide-white/50" : "lg:divide-white";
  const groupBg = isDark ? "lg:bg-[rgb(48,48,48,0.5)]" : "lg:bg-[#ECF0F6]";
  const arrowBtnBg = isDark ? "bg-white" : "bg-white";
  const arrowImgClasses = isDark ? "invert" : "invert";

  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        const el = textRef.current;
        if (el) {
          setIsOverflowing(el.scrollHeight > el.offsetHeight);
        }
      });
    };

    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkOverflow, 150);
    };

    const resizeObserver = new ResizeObserver(debouncedCheck);
    const initialTimeout = setTimeout(checkOverflow, 300);

    if (textRef.current) resizeObserver.observe(textRef.current);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  const shouldShowButton = isOverflowing || isExpanded;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div
      className="success-card col-span-2 flex flex-col justify-start [&_*]:transition-all [&_*]:duration-600 [&_*]:ease-in-out"
      onClick={onClick}
    >
      <div className="image-container hover:[&_.arrow-button]:transform hover:[&_.arrow-button]:rotate-[-45deg] hover:[&_.arrow-button]:bg-[#225BD4] hover:[&_.arrow-button-img]:invert-0 relative overflow-hidden w-full lg:aspect-[16/7] aspect-[1/1]">
        <Image
          src={
            project?.colspan === 2
              ? project?.homepageBg || Images.imagePlaceholder
              : Images.imagePlaceholder
          }
          alt="success-card"
          className="absolute inset-0 w-full h-full object-cover transform"
          fill
          sizes="100vw"
          priority={imagePriority}
        />
        <div
          className={`size-12 bottom-[20px] right-[20px] ${arrowBtnBg} arrow-button absolute rounded-[100px] overflow-hidden flex cursor-pointer`}
        >
          <Image
            className={`size-[15px] arrow-button-img object-cover ${arrowImgClasses} m-auto`}
            src={Icons?.arrowLeft}
            alt=""
            width={15}
            height={15}
          />
        </div>
      </div>

      <h3
        className={`pt-[30px] pb-[10px] self-stretch justify-start ${titleColor} text-[length:var(--sucess-card-title)] font-medium clash-font capitalize leading-[110%]`}
      >
        {project.title}
      </h3>

      {/* Description with read-more */}
      <div className="flex flex-col items-start w-full min-h-[125px] md:min-h-[180px]">
        <p
          ref={textRef}
          className={`self-stretch justify-start ${descColor} lg:text-xl text-base font-medium leading-[180%] transition-all duration-300 ${
            !isExpanded ? "line-clamp-3 overflow-hidden" : ""
          }`}
        >
          {project.description}
        </p>

        {shouldShowButton && (
          <button
            onClick={handleToggle}
            className="mt-2 inline-flex items-center justify-center p-1 rounded-md text-[#225bd4] hover:bg-[#225bd4] hover:text-white transition-all"
            aria-label={isExpanded ? "Read less" : "Read more"}
          >
            {isExpanded ? (
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m18 15-6-6-6 6" />
              </svg>
            ) : (
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            )}
          </button>
        )}
      </div>

      <div
        className={`tags-container xl:gap-[20px] gap-2 max-w-full w-[max-content] mt-[20px] flex-wrap lg:flex-nowrap flex lg:gap-0 ${dividerColor} lg:divide-x-2 ${groupBg} lg:rounded-[50px] lg:overflow-hidden`}
      >
        {project?.categories?.map((cat: any, catIndex) => (
          <div
            key={catIndex}
            className={`tag-item ${tagBg} flex px-5 rounded-2xl lg:bg-transparent lg:px-[30px] lg:rounded-none items-center justify-center xl:h-[49px] h-[35px] ${tagText} font-medium text-center`}
          >
            {cat?.name}
          </div>
        ))}
      </div>
    </div>
  );
}